// The pictures the interface draws things with.
//
// One table, because the strip counts pieces with it and the shelf offers
// recipes with it — two copies would drift the first time a piece changed
// shape, in the one place a player is comparing "what I have" against "what I
// could make".

import {
  BISHOP,
  KING,
  KNIGHT,
  PAWN,
  type PieceType,
  QUEEN,
  ROOK} from "src/engine/rules/board";
import {
  ALTAR,
  SIMPLE_WALL,
  FIRM_WALL,
  REINFORCED_WALL,
  ARMORY,
  COAL_GENERATOR,
  NUCLEAR_GENERATOR,
  BATTERY,
  CABLING_TOWER,
  TRACK,
  TRAIN_CAR,
  TRAIN_ENGINE,
  KEYSTONE,
  RELIC,
  STORE,
  type StructureKind,
  TURRET,
  WELL,
  FUSION_CHAMBER,
  SHIELD_GENERATOR,
  ARTISAN_PILLAR,
  KINETIC_TURRET,
  CANNON_TURRET,
  PROPAGANDA_TOWER,
  HOUSE_OF_CARDS,
  ENDURANCE_MONUMENT,
  FIEND_FOUNTAIN,
  LEGION_TOWER,
  DRONE_WAREHOUSE,
  EMPOWER_TOWER
} from "src/engine/rules/industry/state";
import type { PieceIcon } from "src/api/chrome";

/** The glyph per piece type. White figures, because a count is not a side. */
export const PIECE_GLYPH: Record<PieceType, string> = {
  [PAWN]: "♟",
  [KNIGHT]: "♞",
  [BISHOP]: "♝",
  [ROOK]: "♜",
  [QUEEN]: "♛",
  [KING]: "♚"
};

/**
 * The icon per piece type, where the kit draws one — `PieceMark` reads it and
 * falls back to `PIECE_GLYPH` for the rest. **Placeholders until
 * there is commissioned piece art**, and SVG rather than text on purpose: the
 * chess figures are the one glyph range a common system font claims for its
 * own icons and then fails to draw (Font Awesome, U+265A–U+265F), so a text
 * glyph is blank on some machines and fine on others. A path cannot be.
 *
 * `PIECE_GLYPH` stays the answer wherever the picture is *prose* — a count in
 * a sentence, a snapshot's army line — because an icon has no place in a
 * string.
 */
export const PIECE_ICON: Partial<Record<PieceType, PieceIcon>> = {
  [PAWN]: "pawn",
  [KNIGHT]: "knight",
  [BISHOP]: "bishop",
  [ROOK]: "rook",
  [QUEEN]: "queen",
  [KING]: "king"
};

/**
 * The glyph per structure kind (§26 F6), for the same one-table reason: the
 * strip shows a token in hand with it, the dev menu grants one with it, and
 * two copies would drift in the one place a player is comparing "what I hold"
 * against "what I could stand up". Where a kind carries a power it wears that
 * power's own glyph, so the token and the pip it lights read as one thing.
 */
export const STRUCTURE_GLYPH: Record<StructureKind, string> = {
  [STORE]: "▤",
  [TURRET]: "☖",
  [ALTAR]: "⚱",
  [RELIC]: "⏩",
  [KEYSTONE]: "⌖",
  [WELL]: "◉",
  [SIMPLE_WALL]: "▭",
  [FIRM_WALL]: "▬",
  [REINFORCED_WALL]: "█",
  [ARMORY]: "⚔",
  [COAL_GENERATOR]: "⚡",
  [NUCLEAR_GENERATOR]: "☢",
  [BATTERY]: "▮",
  [CABLING_TOWER]: "⌁",
  [TRACK]: "═",
  [TRAIN_ENGINE]: "◆",
  [TRAIN_CAR]: "▣",
  [FUSION_CHAMBER]: "⚭",
  [SHIELD_GENERATOR]: "⛨",
  [ARTISAN_PILLAR]: "♕",
  [KINETIC_TURRET]: "⛬",
  [CANNON_TURRET]: "⛫",
  [PROPAGANDA_TOWER]: "⚑",
  [HOUSE_OF_CARDS]: "♠",
  [ENDURANCE_MONUMENT]: "⛉",
  [FIEND_FOUNTAIN]: "⛲",
  [LEGION_TOWER]: "⚔",
  [DRONE_WAREHOUSE]: "▦",
  [EMPOWER_TOWER]: "⇈"
};

